import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Typography,
  DialogActions,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DialogProps } from "../../misc/DialogProps";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { DialogTopRightActions } from "../../misc/DialogTopRightActions";
import { DrivingLicenseAuthorityFormForm } from "./DrivingLicenseAuthorityFormForm";
import { EditBase, useRecordContext, useRefresh } from "react-admin";
import { useQueryClient } from "react-query";
import { useMenu } from "../../hooks/useMenu";
import { useDialog } from "../../hooks/useDialog";
import { serverAPI } from "../../api/server.api";

export function EditDrivingLicenseAuthorityFormDialog(dialogProps: DialogProps) {
  const { id } = useRecordContext();
  const queryClient = useQueryClient();
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <>
      <Dialog {...dialogProps}>
        <DialogTitle>Formular bearbeiten</DialogTitle>
        <DialogCloseButton onClick={dialogProps.onClose} />
        <DialogTopRightActions>
          <IconButton onClick={openMenu}>
            <MoreVertIcon />
          </IconButton>
        </DialogTopRightActions>
        <DialogContent>
          <EditBase
            redirect={false}
            resource="drivingLicenseAuthorityForms"
            id={id}
            mutationMode="pessimistic"
            mutationOptions={{
              onSuccess: async () => {
                await queryClient.invalidateQueries(["drivingLicenseAuthorities"]);
                await queryClient.invalidateQueries(["drivingLicenseAuthorityForms"]);
                dialogProps.onClose();
              },
            }}
          >
            <DrivingLicenseAuthorityFormForm mode="edit" />
          </EditBase>
        </DialogContent>
      </Dialog>
      <DialogActionsMenu anchorEl={anchorEl} open={open} closeMenu={closeMenu} onClose={dialogProps.onClose} />
    </>
  );
}

function DialogActionsMenu({
  anchorEl,
  open,
  closeMenu,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  closeMenu: () => void;
  onClose: () => void;
}) {
  const { id } = useRecordContext();
  const [authorityId, formId] = id.toString().split(":");
  const refresh = useRefresh();
  const { dialogProps, openDialog, closeDialog } = useDialog();

  const handleDelete = async () => {
    try {
      if (!authorityId) {
        throw new Error(`Invalid id: ${JSON.stringify(id)} -- expected: "<authorityId>:<formId>"`);
      }

      await serverAPI.deleteDrivingLicenseAuthorityForm({
        authorityId,
        formId: formId,
      });

      refresh();
      showSuccessToast("Formular erfolgreich gelöscht.");
      closeDialog();
      onClose();
    } catch (error) {
      console.error("Error deleting form:", error);
      showErrorToast("Fehler beim Löschen des Formulars");
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            openDialog();
          }}
        >
          Löschen
        </MenuItem>
      </Menu>
      <Dialog {...dialogProps}>
        <DialogTitle>Formular löschen?</DialogTitle>
        <DialogCloseButton onClick={closeDialog} />
        <DialogContent>
          <Typography>
            Bist du dir sicher, dass das Formular gelöscht werden soll? Diese Aktion kann nicht rückgängig gemacht
            werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Abbrechen
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
